import { useState, useMemo } from 'react'
import { Filter, ChevronDown, ChevronUp, X } from 'lucide-react'
import type { MonitorConfig, MonitorEventsParams } from '@/lib/api'
import type { LayerState } from '@/components/monitor/LayerToggles'

const SEVERITY_BADGE: Record<string, string> = {
  critical: 'bg-red-500/20 text-red-400 border-red-500/40',
  high: 'bg-orange-500/20 text-orange-400 border-orange-500/40',
  medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40',
  low: 'bg-blue-500/20 text-blue-400 border-blue-500/40',
  info: 'bg-gray-500/20 text-gray-400 border-gray-500/40',
}

const DOMAIN_BADGE: Record<string, string> = {
  geopolitical: 'bg-red-500/20 text-red-400 border-red-500/40',
  cyber: 'bg-purple-500/20 text-purple-400 border-purple-500/40',
  infrastructure: 'bg-amber-500/20 text-amber-400 border-amber-500/40',
}

/** Event-type options per layer focus. */
const LAYER_EVENT_TYPES: Record<string, { label: string; value: string }[]> = {
  conflict: [
    { label: 'Conflict', value: 'conflict' },
    { label: 'Explosion', value: 'explosion' },
    { label: 'Protest', value: 'protest' },
    { label: 'Strategic', value: 'strategic_development' },
  ],
  infrastructure: [
    { label: 'Outage', value: 'infrastructure_outage' },
  ],
  outages: [
    { label: 'Outage', value: 'infrastructure_outage' },
  ],
  natural: [
    { label: 'Disaster', value: 'disaster' },
  ],
  weather: [
    { label: 'Disaster', value: 'disaster' },
  ],
  cyber: [
    { label: 'Cyber attack', value: 'cyber_attack' },
    { label: 'Vulnerability', value: 'vulnerability' },
    { label: 'Ransomware', value: 'ransomware' },
  ],
  economic: [
    { label: 'Economic', value: 'economic' },
  ],
}

const LAYER_DOMAINS: Record<string, string[]> = {
  conflict: ['geopolitical'],
  natural: ['geopolitical'],
  weather: ['geopolitical'],
  cyber: ['cyber'],
  infrastructure: ['infrastructure'],
  outages: ['infrastructure'],
  economic: ['infrastructure'],
  gpsjam: [],
}

interface EventFiltersProps {
  config: MonitorConfig | null
  params: MonitorEventsParams
  onChange: (params: MonitorEventsParams) => void
  activeLayers?: LayerState
}

export default function EventFilters({ config, params, onChange, activeLayers }: EventFiltersProps) {
  const [expanded, setExpanded] = useState(true)

  const selectedDomains = params.domains?.split(',').filter(Boolean) || []
  const selectedSeverities = params.severities?.split(',').filter(Boolean) || []
  const selectedEventTypes = params.event_types?.split(',').filter(Boolean) || []

  const { relevantDomains, relevantEventTypes } = useMemo(() => {
    const defDomains = config?.available_domains || ['geopolitical', 'cyber', 'infrastructure']
    if (!activeLayers) {
      return { relevantDomains: defDomains, relevantEventTypes: [] }
    }
    const active: (keyof LayerState)[] = (Object.keys(activeLayers) as (keyof LayerState)[]).filter(
      (k) => activeLayers[k]
    )
    const domains = new Set<string>()
    const eventTypeOpts: { label: string; value: string }[] = []
    const seen = new Set<string>()
    for (const k of active) {
      for (const d of LAYER_DOMAINS[k] || []) domains.add(d)
      for (const opt of LAYER_EVENT_TYPES[k] || []) {
        if (!seen.has(opt.value)) {
          seen.add(opt.value)
          eventTypeOpts.push(opt)
        }
      }
    }
    return {
      relevantDomains: domains.size > 0 ? Array.from(domains) : defDomains,
      relevantEventTypes: eventTypeOpts,
    }
  }, [activeLayers, config?.available_domains, config?.available_event_types])

  const toggleDomain = (d: string) => {
    const next = selectedDomains.includes(d)
      ? selectedDomains.filter((x) => x !== d)
      : [...selectedDomains, d]
    onChange({ ...params, domains: next.length ? next.join(',') : undefined, page: 1 })
  }

  const toggleSeverity = (s: string) => {
    const next = selectedSeverities.includes(s)
      ? selectedSeverities.filter((x) => x !== s)
      : [...selectedSeverities, s]
    onChange({ ...params, severities: next.length ? next.join(',') : undefined, page: 1 })
  }

  const toggleEventType = (t: string) => {
    const next = selectedEventTypes.includes(t)
      ? selectedEventTypes.filter((x) => x !== t)
      : [...selectedEventTypes, t]
    onChange({ ...params, event_types: next.length ? next.join(',') : undefined, page: 1 })
  }

  const setTimeRange = (hours: number | null) => {
    if (hours === null) {
      onChange({ ...params, time_from: undefined, time_to: undefined, page: 1 })
      return
    }
    const now = new Date()
    const from = new Date(now.getTime() - hours * 60 * 60 * 1000)
    onChange({ ...params, time_from: from.toISOString(), time_to: undefined, page: 1 })
  }

  const setRegion = (region: string | undefined) => {
    onChange({ ...params, region, page: 1 })
  }

  const clearAll = () => {
    onChange({ page: 1, page_size: params.page_size })
  }

  const hasFilters = !!(params.domains || params.severities || params.event_types || params.region || params.time_from || params.search)

  const timeRanges = [
    { label: '1h', hours: 1 },
    { label: '6h', hours: 6 },
    { label: '24h', hours: 24 },
    { label: '7d', hours: 168 },
    { label: '30d', hours: 720 },
    { label: 'All', hours: null as number | null },
  ]

  return (
    <div className="bg-gray-950/95 backdrop-blur border border-green-900/50 rounded overflow-hidden shadow-[0_0_15px_rgba(34,197,94,0.06)]">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-mono font-medium text-gray-400 hover:bg-green-500/10 transition-colors"
      >
        <span className="flex items-center gap-2">
          <Filter className="w-3.5 h-3.5" />
          <span className="uppercase tracking-wider">Filters</span>
          {hasFilters && (
            <span className="bg-green-500/20 text-green-400 text-[8px] px-1.5 py-0.5 rounded border border-green-500/40 drop-shadow-[0_0_4px_rgba(74,222,128,0.2)]">Active</span>
          )}
        </span>
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-3">
          {/* Time range */}
          <div>
            <label className="text-[9px] text-gray-600 uppercase tracking-[0.15em] font-mono mb-1.5 block">Time Range</label>
            <div className="flex flex-wrap gap-1">
              {timeRanges.map((t) => (
                <button
                  key={t.label}
                  onClick={() => setTimeRange(t.hours)}
                  className={`px-2 py-0.5 text-[9px] font-mono rounded border transition-all ${
                    (t.hours === null && !params.time_from) ||
                    (t.hours !== null && params.time_from && Math.abs(new Date().getTime() - new Date(params.time_from).getTime() - t.hours * 3600000) < 600000)
                      ? 'bg-green-500/20 text-green-400 border-green-500/40'
                      : 'bg-gray-800/50 text-gray-500 border-gray-700/40 hover:border-gray-600/50'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Domains — filtered by active layers */}
          <div>
            <label className="text-[9px] text-gray-600 uppercase tracking-[0.15em] font-mono mb-1.5 block">Domain</label>
            <div className="flex flex-wrap gap-1">
              {relevantDomains.map((d) => (
                <button
                  key={d}
                  onClick={() => toggleDomain(d)}
                  className={`px-2 py-0.5 text-[9px] font-mono rounded border transition-all capitalize ${
                    selectedDomains.includes(d)
                      ? DOMAIN_BADGE[d] || 'bg-blue-500/20 text-blue-300 border-blue-500/40'
                      : 'bg-gray-800/50 text-gray-500 border-gray-700/40 hover:border-gray-600/50'
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          {/* Event Type — shown when layers provide specific types */}
          {relevantEventTypes.length > 0 && (
            <div>
              <label className="text-[9px] text-gray-600 uppercase tracking-[0.15em] font-mono mb-1.5 block">Event Type</label>
              <div className="flex flex-wrap gap-1">
                {relevantEventTypes.map(({ label, value }) => (
                  <button
                    key={value}
                    onClick={() => toggleEventType(value)}
                    className={`px-2 py-0.5 text-[9px] font-mono rounded border transition-all ${
                      selectedEventTypes.includes(value)
                        ? 'bg-green-500/20 text-green-400 border-green-500/40'
                        : 'bg-gray-800/50 text-gray-500 border-gray-700/40 hover:border-gray-600/50'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Severity — label adapts to layer focus */}
          <div>
            <label className="text-[9px] text-gray-600 uppercase tracking-[0.15em] font-mono mb-1.5 block">
              {activeLayers?.conflict ? 'Conflict severity' :
               activeLayers?.natural || activeLayers?.weather ? 'Disaster severity' :
               activeLayers?.infrastructure || activeLayers?.outages ? 'Outage severity' :
               'Severity'}
            </label>
            <div className="flex flex-wrap gap-1">
              {(config?.available_severities || ['critical', 'high', 'medium', 'low', 'info']).map((s) => (
                <button
                  key={s}
                  onClick={() => toggleSeverity(s)}
                  className={`px-2 py-0.5 text-[9px] font-mono rounded border transition-all capitalize ${
                    selectedSeverities.includes(s)
                      ? SEVERITY_BADGE[s] || 'bg-blue-500/20 text-blue-300 border-blue-500/40'
                      : 'bg-gray-800/50 text-gray-500 border-gray-700/40 hover:border-gray-600/50'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Region */}
          {(config?.available_regions?.length ?? 0) > 0 && (
            <div>
              <label className="text-[9px] text-gray-600 uppercase tracking-[0.15em] font-mono mb-1.5 block">Region</label>
              <select
                value={params.region || ''}
                onChange={(e) => setRegion(e.target.value || undefined)}
                className="w-full bg-gray-800/70 border border-gray-700/40 rounded px-2 py-1 text-[10px] text-gray-400 font-mono focus:outline-none focus:border-green-500/50 focus:ring-1 focus:ring-green-400/20"
              >
                <option value="">All regions</option>
                {(config?.available_regions || []).map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          )}

          {hasFilters && (
            <button
              onClick={clearAll}
              className="flex items-center gap-1 text-[9px] font-mono text-gray-600 hover:text-gray-300 transition-colors"
            >
              <X className="w-3 h-3" /> CLEAR FILTERS
            </button>
          )}
        </div>
      )}
    </div>
  )
}
